package com.fantasytenis.web.screens.survivor

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.survivor.entity.*
import com.fantasytenis.shared.domain.survivor.usecases.SurvivorContestDetailData
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen showing details of a survivor contest and allowing entry.
 */
@Composable
fun SurvivorContestDetailScreen(contestId: String) {
    val scope = rememberCoroutineScope()
    var contestData by remember { mutableStateOf<SurvivorContestDetailData?>(null) }
    var userEntries by remember { mutableStateOf<List<SurvivorEntryEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var isEntering by remember { mutableStateOf(false) }

    // Load contest data
    LaunchedEffect(contestId) {
        isLoading = true
        error = null

        // Load contest detail data
        DependencyContainer.getSurvivorContestDetailUseCase(contestId).fold(
            onSuccess = { data ->
                contestData = data
            },
            onFailure = { e ->
                error = e.message
            }
        )

        // Load user's entries for this contest
        DependencyContainer.getUserSurvivorEntriesUseCase(contestId).fold(
            onSuccess = { entries ->
                userEntries = entries
                isLoading = false
            },
            onFailure = { e ->
                // User might not have entries, that's OK
                isLoading = false
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = contestData?.contest?.name ?: "Contest Details",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.SurvivorContests) },
            customActions = {
                // Leaderboard button (visible on desktop)
                Button(
                    text = "Leaderboard",
                    onClick = { NavigationState.navigate(Route.SurvivorLeaderboard(contestId)) },
                    variant = "secondary"
                )
            }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1000.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.getSurvivorContestDetailUseCase(contestId).fold(
                            onSuccess = { contestData = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                contestData != null -> {
                    val contest = contestData!!.contest
                    val roundSummaries = contestData!!.roundSummaries

                    // Contest Info Card
                    ContestInfoCard(contest)

                    // User's Entries Section
                    if (userEntries.isNotEmpty()) {
                        UserEntriesSection(userEntries, contest)
                    }

                    // Enter Contest Section
                    if (contest.canEnter) {
                        val canEnterMore = userEntries.size < contest.maxEntriesPerUser
                        if (canEnterMore) {
                            EnterContestSection(
                                contest = contest,
                                isEntering = isEntering,
                                onEnter = {
                                    scope.launch {
                                        isEntering = true
                                        DependencyContainer.enterSurvivorContestUseCase(contestId).fold(
                                            onSuccess = { entry ->
                                                // Navigate to pick selection if contest is active
                                                if (contest.isActive) {
                                                    NavigationState.navigate(
                                                        Route.SurvivorPickSelection(contestId, entry.id)
                                                    )
                                                } else {
                                                    // Reload to show new entry
                                                    userEntries = userEntries + entry
                                                    isEntering = false
                                                }
                                            },
                                            onFailure = { e ->
                                                error = e.message
                                                isEntering = false
                                            }
                                        )
                                    }
                                }
                            )
                        }
                    }

                    // Round Summaries Section
                    if (roundSummaries.isNotEmpty()) {
                        RoundSummariesSection(roundSummaries, contest)
                    }

                    // Rules Section
                    RulesSection(contest)

                    // Payout Structure Section
                    if (contest.payoutStructure.isNotEmpty()) {
                        PayoutStructureSection(contest.payoutStructure, contest.prizePool)
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun ContestInfoCard(contest: SurvivorContestEntity) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Status and variation badges
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(16.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            StatusBadge(contest.status)
            VariationBadge(contest.variation)
            if (contest.isFeatured) {
                Span({
                    style {
                        backgroundColor(Colors.Secondary)
                        color(Colors.TextPrimary)
                        fontSize(11.px)
                        fontWeight(600)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("FEATURED")
                }
            }
        }

        // Tournament name
        H2({
            style {
                fontSize(24.px)
                fontWeight(700)
                color(Colors.TextPrimary)
                marginBottom(4.px)
                margin(0.px)
            }
        }) {
            Text(contest.name)
        }

        P({
            style {
                color(Colors.TextSecondary)
                fontSize(14.px)
                marginBottom(16.px)
                margin(0.px)
                marginTop(4.px)
            }
        }) {
            Text(contest.tournamentName)
        }

        contest.description?.let { desc ->
            P({
                style {
                    color(Colors.TextSecondary)
                    fontSize(14.px)
                    marginBottom(16.px)
                    margin(0.px)
                    marginBottom(16.px)
                }
            }) {
                Text(desc)
            }
        }

        // Stats grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(140px, 1fr))")
                gap(16.px)
                marginBottom(16.px)
            }
        }) {
            InfoBox("Entry Fee", contest.entryFeeDisplay)
            InfoBox("Prize Pool", contest.prizePoolDisplay)
            InfoBox("Entries", "${contest.currentEntries}/${contest.maxEntries ?: "∞"}")
            InfoBox("Alive", "${contest.aliveEntries}", if (contest.isActive) Colors.Success else null)
        }

        // Current round info if active
        if (contest.isActive && contest.currentRound != null) {
            Div({
                style {
                    padding(16.px)
                    backgroundColor(rgb(232, 245, 233))
                    borderRadius(8.px)
                    marginTop(16.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Div {
                        Div({
                            style {
                                fontWeight(600)
                                fontSize(14.px)
                                color(Colors.TextPrimary)
                            }
                        }) {
                            Text("Current Round: ${contest.currentRound!!.displayName}")
                        }
                        contest.roundDeadline?.let { deadline ->
                            Div({
                                style {
                                    fontSize(13.px)
                                    color(Colors.TextSecondary)
                                    marginTop(4.px)
                                }
                            }) {
                                Text("Pick deadline: $deadline")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatusBadge(status: SurvivorContestStatus) {
    val (bgColor, textColor, label) = when (status) {
        SurvivorContestStatus.UPCOMING -> Triple(rgb(227, 242, 253), rgb(25, 118, 210), "Upcoming")
        SurvivorContestStatus.ACTIVE -> Triple(rgb(232, 245, 233), rgb(56, 142, 60), "Active")
        SurvivorContestStatus.COMPLETED -> Triple(rgb(245, 245, 245), rgb(117, 117, 117), "Completed")
        SurvivorContestStatus.CANCELLED -> Triple(rgb(255, 235, 238), rgb(211, 47, 47), "Cancelled")
        SurvivorContestStatus.PAYING_OUT -> Triple(rgb(255, 243, 224), rgb(245, 124, 0), "Paying Out")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(12.px)
            fontWeight(600)
            padding(4.px, 12.px)
            borderRadius(4.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun VariationBadge(variation: SurvivorVariation) {
    val (bgColor, textColor, label) = when (variation) {
        SurvivorVariation.STRAIGHT_KNOCKOUT -> Triple(rgb(255, 235, 238), rgb(211, 47, 47), "Knockout")
        SurvivorVariation.STRIKE_SYSTEM -> Triple(rgb(255, 243, 224), rgb(245, 124, 0), "Strikes")
        SurvivorVariation.REBUY_ALLOWED -> Triple(rgb(232, 245, 233), rgb(56, 142, 60), "Rebuy")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(12.px)
            fontWeight(600)
            padding(4.px, 12.px)
            borderRadius(4.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun InfoBox(label: String, value: String, valueColor: CSSColorValue? = null) {
    Div({
        style {
            backgroundColor(Colors.SurfaceVariant)
            padding(16.px)
            borderRadius(8.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(12.px)
                color(Colors.TextSecondary)
                marginBottom(4.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(valueColor ?: Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun UserEntriesSection(entries: List<SurvivorEntryEntity>, contest: SurvivorContestEntity) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(16.px)
                margin(0.px)
            }
        }) {
            Text("Your Entries")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            entries.forEach { entry ->
                EntryRow(entry, contest)
            }
        }
    }
}

@Composable
private fun EntryRow(entry: SurvivorEntryEntity, contest: SurvivorContestEntity) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(12.px, 16.px)
            backgroundColor(Colors.SurfaceVariant)
            borderRadius(8.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            // Status indicator
            EntryStatusBadge(entry.status)

            Div {
                Div({
                    style {
                        fontWeight(500)
                        fontSize(14.px)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text("Entry #${entry.id.takeLast(4)}")
                }
                Div({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    val statusText = when (entry.status) {
                        SurvivorEntryStatus.ALIVE -> "Survived ${entry.survivedRounds} rounds"
                        SurvivorEntryStatus.ELIMINATED -> "Eliminated in ${entry.eliminatedInRound?.displayName ?: "?"}"
                        SurvivorEntryStatus.REBUY_ELIGIBLE -> "Eliminated - Rebuy available"
                        SurvivorEntryStatus.WITHDRAWN -> "Withdrawn"
                        SurvivorEntryStatus.WINNER -> "Winner! - Rank #${entry.rank}"
                        SurvivorEntryStatus.PAID -> "Won ${entry.payoutDisplay}!"
                    }
                    Text(statusText)
                }
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
            }
        }) {
            // Strike indicator for strike system
            if (contest.variation == SurvivorVariation.STRIKE_SYSTEM && entry.isAlive) {
                StrikeIndicator(entry.strikesUsed, contest.strikeLimit)
            }

            // Action button
            when {
                entry.isAlive && contest.isActive -> {
                    Button(
                        text = "Make Pick",
                        onClick = {
                            NavigationState.navigate(
                                Route.SurvivorPickSelection(contest.id, entry.id)
                            )
                        },
                        variant = "primary"
                    )
                }
                entry.canRebuy && contest.variation == SurvivorVariation.REBUY_ALLOWED -> {
                    Button(
                        text = "Rebuy ${contest.rebuyFeeDisplay}",
                        onClick = {
                            // TODO: Implement rebuy
                        },
                        variant = "secondary"
                    )
                }
                else -> {
                    Button(
                        text = "View Picks",
                        onClick = {
                            NavigationState.navigate(
                                Route.SurvivorEntry(contest.id, entry.id)
                            )
                        },
                        variant = "secondary"
                    )
                }
            }
        }
    }
}

@Composable
private fun EntryStatusBadge(status: SurvivorEntryStatus) {
    val (bgColor, textColor) = when (status) {
        SurvivorEntryStatus.ALIVE -> rgb(232, 245, 233) to rgb(56, 142, 60)
        SurvivorEntryStatus.ELIMINATED -> rgb(255, 235, 238) to rgb(211, 47, 47)
        SurvivorEntryStatus.REBUY_ELIGIBLE -> rgb(255, 243, 224) to rgb(245, 124, 0)
        SurvivorEntryStatus.WITHDRAWN -> rgb(245, 245, 245) to rgb(117, 117, 117)
        SurvivorEntryStatus.WINNER -> rgb(255, 243, 224) to rgb(245, 124, 0)
        SurvivorEntryStatus.PAID -> rgb(232, 245, 233) to rgb(56, 142, 60)
    }

    Span({
        style {
            width(10.px)
            height(10.px)
            borderRadius(50.percent)
            backgroundColor(textColor)
        }
    })
}

@Composable
private fun StrikeIndicator(strikesUsed: Int, strikeLimit: Int) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(4.px)
            alignItems(AlignItems.Center)
        }
    }) {
        repeat(strikeLimit) { index ->
            val isUsed = index < strikesUsed
            Span({
                style {
                    fontSize(16.px)
                    color(if (isUsed) Colors.Error else Colors.Gray300)
                }
            }) {
                Text(if (isUsed) "✖" else "♥")
            }
        }
    }
}

@Composable
private fun EnterContestSection(
    contest: SurvivorContestEntity,
    isEntering: Boolean,
    onEnter: () -> Unit
) {
    Div({
        style {
            backgroundColor(rgb(232, 245, 233))
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            textAlign("center")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(8.px)
                margin(0.px)
            }
        }) {
            Text("Ready to compete?")
        }
        P({
            style {
                color(Colors.TextSecondary)
                fontSize(14.px)
                marginBottom(16.px)
                margin(0.px)
                marginTop(8.px)
            }
        }) {
            Text("Entry fee: ${contest.entryFeeDisplay} • ${contest.variationDescription}")
        }
        Button(
            text = if (isEntering) "Entering..." else "Enter Contest",
            onClick = onEnter,
            variant = "primary"
        )
    }
}

@Composable
private fun RoundSummariesSection(
    summaries: List<SurvivorRoundSummaryEntity>,
    contest: SurvivorContestEntity
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(16.px)
                margin(0.px)
            }
        }) {
            Text("Round Progress")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            summaries.sortedBy { it.round.ordinal }.forEach { summary ->
                RoundSummaryRow(summary, summary.round == contest.currentRound)
            }
        }
    }
}

@Composable
private fun RoundSummaryRow(summary: SurvivorRoundSummaryEntity, isCurrent: Boolean) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(12.px, 16.px)
            backgroundColor(if (isCurrent) rgb(232, 245, 233) else Colors.SurfaceVariant)
            borderRadius(8.px)
            if (isCurrent) {
                border(2.px, LineStyle.Solid, Colors.Primary)
            }
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            Div({
                style {
                    fontWeight(600)
                    fontSize(14.px)
                    color(Colors.TextPrimary)
                }
            }) {
                Text(summary.round.displayName)
            }
            if (isCurrent) {
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(10.px)
                        fontWeight(600)
                        padding(2.px, 6.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("CURRENT")
                }
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(16.px)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text(summary.statusDisplay)
            }
            Div({
                style {
                    fontSize(13.px)
                    color(if (summary.eliminatedCount > 0) Colors.Error else Colors.TextSecondary)
                }
            }) {
                Text("${summary.aliveAfter} alive")
                if (summary.eliminatedCount > 0) {
                    Text(" (-${summary.eliminatedCount})")
                }
            }
        }
    }
}

@Composable
private fun RulesSection(contest: SurvivorContestEntity) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(16.px)
                margin(0.px)
            }
        }) {
            Text("Contest Rules")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            RuleItem("Pick one player per round to win their match")
            RuleItem("Each player can only be used once per contest")

            when (contest.variation) {
                SurvivorVariation.STRAIGHT_KNOCKOUT -> {
                    RuleItem("One loss and you're eliminated", Colors.Error)
                }
                SurvivorVariation.STRIKE_SYSTEM -> {
                    RuleItem("You have ${contest.strikeLimit} strikes before elimination", Colors.Warning)
                }
                SurvivorVariation.REBUY_ALLOWED -> {
                    RuleItem("Rebuy available through ${contest.rebuyDeadlineRound?.displayName ?: "R32"} for ${contest.rebuyFeeDisplay}", Colors.Primary)
                }
            }

            RuleItem("If you don't make a pick before the deadline, you're automatically eliminated")
            RuleItem("If your player withdraws, you receive a walkover loss")
            RuleItem("Last survivor(s) split the prize pool")
        }
    }
}

@Composable
private fun RuleItem(text: String, highlightColor: CSSColorValue? = null) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(8.px)
        }
    }) {
        Span({
            style {
                color(highlightColor ?: Colors.Primary)
                fontSize(14.px)
            }
        }) {
            Text("•")
        }
        Span({
            style {
                fontSize(14.px)
                color(if (highlightColor != null) highlightColor else Colors.TextPrimary)
            }
        }) {
            Text(text)
        }
    }
}

@Composable
private fun PayoutStructureSection(
    payouts: List<SurvivorPayoutTierEntity>,
    prizePool: Int
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(16.px)
                margin(0.px)
            }
        }) {
            Text("Payout Structure")
        }

        Table({
            style {
                width(100.percent)
                property("border-collapse", "collapse")
            }
        }) {
            Thead {
                Tr {
                    Th({
                        style {
                            textAlign("left")
                            padding(8.px)
                            property("border-bottom", "1px solid ${Colors.Gray300}")
                            color(Colors.TextSecondary)
                            fontSize(12.px)
                        }
                    }) {
                        Text("Place")
                    }
                    Th({
                        style {
                            textAlign("right")
                            padding(8.px)
                            property("border-bottom", "1px solid ${Colors.Gray300}")
                            color(Colors.TextSecondary)
                            fontSize(12.px)
                        }
                    }) {
                        Text("Prize")
                    }
                }
            }
            Tbody {
                payouts.forEach { tier ->
                    Tr {
                        Td({
                            style {
                                padding(8.px)
                                property("border-bottom", "1px solid ${Colors.Gray100}")
                            }
                        }) {
                            val placeText = if (tier.minRank == tier.maxRank) {
                                "#${tier.minRank}"
                            } else {
                                "#${tier.minRank} - #${tier.maxRank}"
                            }
                            Text(placeText)
                        }
                        Td({
                            style {
                                textAlign("right")
                                padding(8.px)
                                property("border-bottom", "1px solid ${Colors.Gray100}")
                                fontWeight(500)
                                color(Colors.Primary)
                            }
                        }) {
                            val amount = tier.amount
                                ?: (tier.percentage?.let { (it * prizePool / 100).toInt() })
                                ?: 0
                            Text("$${amount / 100.0}")
                        }
                    }
                }
            }
        }
    }
}
